"use client";

import { useEffect, useState, useRef } from 'react';
import { useChatContext } from '@/contexts/ChatContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { LayoutState, PositionState, UserDto } from '@/types';
import LeftPanel from './LeftPanel';
import CenterPanel, { CenterPanelRef } from './CenterPanel';
import RightPanel from './RightPanel';
import ImageViewer from './ImageViewer';
import VideoViewer from './VideoViewer';
import {
    MessageCircle,
    X,
    Minus,
    Maximize2,
    Search,
    Users
} from 'lucide-react';
import MessageToast from './MessageToast';
import styles from './ChatWidget.module.css';
import { notificationService } from '@/services/NotificationService';
import { getRoomDisplayName } from '@/lib/utils';

interface ChatWidgetProps {
    userID: string;
    jfcToken: string;
    userInfo?: Partial<UserDto>;
    groupID?: string;       // OpenIM groupID = customerId
    groupName?: string;
    customerNo?: string;    // For display only (e.g. "14-0000")
    mode?: 'widget' | 'fullscreen';
    onClose?: () => void;
}

type PendingOpenIntent = string | {
    groupID?: string;
    url?: string;
    source?: string;
    timestamp?: number;
};

const PENDING_OPEN_INTENT_TTL_MS = 60000;

export default function ChatWidget({ userID, jfcToken, userInfo, groupID, groupName, customerNo, mode = 'widget', onClose }: ChatWidgetProps) {
    const {
        init,
        updateGroupFilter,
        connectionStatus,
        layoutState,
        setLayoutState,
        setPositionState,
        selectedRoom,
        rooms,
        selectRoom,
        getRoomUnreadCount,
        imageViewer,
        videoViewer,
        isActiveMember
    } = useChatContext();

    const { t } = useLanguage();
    const [isBrowserOnline, setIsBrowserOnline] = useState(() => (
        typeof navigator === 'undefined' ? true : navigator.onLine
    ));

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const handleOnline = () => setIsBrowserOnline(true);
        const handleOffline = () => setIsBrowserOnline(false);

        setIsBrowserOnline(typeof navigator === 'undefined' ? true : navigator.onLine);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Helper to translate connection status
    const getTranslatedConnectionStatus = (status: string): string => {
        if (!isBrowserOnline) return t.checkInternetConnection;

        switch (status) {
            case 'Disconnected': return t.disconnected;
            case 'Connecting...': return t.connecting;
            case 'Connected': return t.connected;
            case 'Connection Failed': return t.connectionFailed;
            case 'Kicked Offline': return t.kickedOffline;
            case 'Token Expired': return t.sessionExpired;
            default: return status;
        }
    };

    const getConnectionStatusClassName = () => {
        const statusClass = isBrowserOnline
            ? connectionStatus.toLowerCase().replace(/[^a-z]/g, '')
            : 'connectionfailed';

        return `${styles.connectionStatus} ${styles[statusClass] || ''}`;
    };

    const totalUnread = rooms.reduce((sum, r) => sum + getRoomUnreadCount(r), 0);
    const connectionStatusLabel = getTranslatedConnectionStatus(connectionStatus);
    const shouldShowConnectionStatus = connectionStatusLabel !== t.connected;

    const [isInitializing, setIsInitializing] = useState(true);
    const widgetContainerRef = useRef<HTMLDivElement>(null);
    const centerPanelRef = useRef<CenterPanelRef>(null);
    const originalTitleRef = useRef<string>('');
    const hasInitializedRef = useRef<boolean>(false);
    const lastUserIDRef = useRef<string>('');
    const lastJfcTokenRef = useRef<string>('');
    const lastGroupIDRef = useRef<string | undefined>(undefined);
    const lastGroupNameRef = useRef<string | undefined>(undefined);
    const accountKey = userInfo?.email?.trim().toLowerCase() || userID;

    // Initialize on mount or when account identity changes (NOT when token or groupID changes)
    // Token updates are handled via jfc-chat:group-change event in ChatContext
    useEffect(() => {
        // Skip if already initialized with the same user
        if (hasInitializedRef.current && lastUserIDRef.current === accountKey) {
            return;
        }

        // Close the chat popup when switching to a different user
        // so stale data from the old account isn't visible during re-init
        if (hasInitializedRef.current && lastUserIDRef.current !== accountKey) {
            setLayoutState(LayoutState.Icon);
        }

        const initChat = async () => {
            setIsInitializing(true);
            hasInitializedRef.current = true;
            lastUserIDRef.current = accountKey;
            lastJfcTokenRef.current = jfcToken;
            // init() already applies the initial group filter. Track it here so the
            // group-change effect only closes the popup for real account group changes.
            lastGroupIDRef.current = groupID;
            lastGroupNameRef.current = groupName;
            await init(userID, jfcToken, userInfo, groupID, groupName, customerNo);
            setIsInitializing(false);
        };
        initChat();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountKey]);

    // Update group filter when groupID changes (without re-initializing SDK)
    useEffect(() => {
        // Skip on initial render or if not yet initialized
        // DON'T update the ref here - we want to detect the change when isInitializing becomes false
        if (!hasInitializedRef.current || isInitializing) {
            return;
        }

        // Only update if groupID actually changed
        if (lastGroupIDRef.current !== groupID) {
            // Close the chat popup when switching groups
            // so stale data from the old group isn't visible
            setLayoutState(LayoutState.Icon);

            lastGroupIDRef.current = groupID;
            lastGroupNameRef.current = groupName;

            // Call updateGroupFilter with a small delay to ensure SDK state is stable
            // This prevents race conditions when switching groups rapidly
            const timeoutId = setTimeout(async () => {
                await updateGroupFilter(groupID || null, groupName || null, customerNo);
            }, 100);

            return () => clearTimeout(timeoutId);
        }
    }, [groupID, groupName, isInitializing, updateGroupFilter]);

    // Auto-select first room when rooms are loaded, and correct any stale
    // selection left behind by a customer/group filter change.
    useEffect(() => {
        if (isInitializing || rooms.length === 0) return;

        const selectedRoomStillAvailable = selectedRoom &&
            rooms.some(room => room.groupID === selectedRoom.groupID);

        if (!selectedRoomStillAvailable) {
            selectRoom(rooms[0]);
        }
    }, [isInitializing, rooms, selectedRoom, selectRoom]);

    // Listen for OPEN_CHAT event from host app
    useEffect(() => {
        const handleOpenChat = (event: any) => {
            const targetGroupID = event.detail?.groupID;

            const persistPendingOpenIntent = () => {
                if (!targetGroupID) return;
                (window as any)._jfcChatPendingOpenIntent = {
                    ...event.detail,
                    groupID: targetGroupID,
                    timestamp: Date.now(),
                };
            };

            if (layoutState === LayoutState.Close || layoutState === LayoutState.Icon) {
                setLayoutState(LayoutState.Minimize);
                setPositionState(PositionState.Widget);
            } else if (layoutState === LayoutState.Minibar) {
                setLayoutState(LayoutState.Minimize);
            } else if (layoutState === LayoutState.Minimize) {
                // Already minimized (open), do nothing or bring to focus
            }

            // If groupID is provided in the event, try to find and select that room
            if (targetGroupID) {
                if (rooms.length === 0) {
                    persistPendingOpenIntent();
                    return;
                }

                const targetRoom = rooms.find(r => r.groupID === targetGroupID);
                if (targetRoom) {
                    selectRoom(targetRoom);
                    if ((window as any)._jfcChatPendingOpenIntent !== undefined) {
                        delete (window as any)._jfcChatPendingOpenIntent;
                    }
                } else {
                    persistPendingOpenIntent();
                }
                return;
            }

            if ((window as any)._jfcChatPendingOpenIntent !== undefined) {
                delete (window as any)._jfcChatPendingOpenIntent;
            }
        };

        window.addEventListener('jfc-chat:open-chat', handleOpenChat);

        const pendingIntent = (window as any)._jfcChatPendingOpenIntent as PendingOpenIntent | undefined;
        if (pendingIntent !== undefined) {
            const pendingGroupID = typeof pendingIntent === 'string'
                ? pendingIntent
                : pendingIntent?.groupID;
            const pendingTimestamp = typeof pendingIntent === 'string'
                ? undefined
                : pendingIntent?.timestamp;
            if (pendingTimestamp && Date.now() - pendingTimestamp > PENDING_OPEN_INTENT_TTL_MS) {
                delete (window as any)._jfcChatPendingOpenIntent;
            } else {
                handleOpenChat(new CustomEvent('jfc-chat:open-chat', {
                    detail: {
                        ...(typeof pendingIntent === 'string' ? {} : pendingIntent),
                        groupID: pendingGroupID
                    }
                }));
            }
        }

        const handleCloseChat = () => {
            if (layoutState !== LayoutState.Close && layoutState !== LayoutState.Icon) {
                setLayoutState(LayoutState.Icon);
            }
        };
        window.addEventListener('jfc-chat:close-chat', handleCloseChat);

        return () => {
            window.removeEventListener('jfc-chat:open-chat', handleOpenChat);
            window.removeEventListener('jfc-chat:close-chat', handleCloseChat);
        };
    }, [layoutState, setLayoutState, setPositionState, rooms, selectRoom]);

    // Track last reconnect attempt to prevent rapid retries
    const lastReconnectAttempt = useRef<number>(0);
    const reconnectAttempts = useRef<number>(0);

    // Auto-reconnect when opening the widget if connection failed (with debounce)
    useEffect(() => {
        const isWidgetOpen = layoutState === LayoutState.Minimize || layoutState === LayoutState.Maximize;
        if (!isBrowserOnline) return;

        if (isWidgetOpen && connectionStatus === 'Connection Failed') {
            const now = Date.now();
            // Exponential backoff: 5s, 10s, 20s, 40s, max 60s
            const minDelay = Math.min(5000 * Math.pow(2, reconnectAttempts.current), 60000);

            if (now - lastReconnectAttempt.current < minDelay) {
                // Too soon, don't reconnect yet
                return;
            }

            const initChat = async () => {
                lastReconnectAttempt.current = Date.now();
                reconnectAttempts.current += 1;
                setIsInitializing(true);
                const success = await init(userID, jfcToken, userInfo, groupID, groupName, customerNo);
                setIsInitializing(false);
                // Reset attempts on success
                if (success) {
                    reconnectAttempts.current = 0;
                }
            };
            initChat();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layoutState, connectionStatus, accountKey, jfcToken, isBrowserOnline]);

    // Reset reconnect attempts when connection succeeds
    useEffect(() => {
        if (connectionStatus === 'Connected') {
        reconnectAttempts.current = 0;
        }
    }, [connectionStatus]);

    // Store original page title once on mount
    useEffect(() => {
        originalTitleRef.current = document.title.replace(/^\(\d+\)\s+/, '');
        return () => { document.title = originalTitleRef.current; };
    }, []);

    // Update document.title with unread count (like Messenger)
    useEffect(() => {
        if (!originalTitleRef.current) return;
        document.title = totalUnread > 0
            ? `(${totalUnread}) ${originalTitleRef.current}`
            : originalTitleRef.current;
    }, [totalUnread]);

    // Close chat when clicking outside (on top/bottom bars) when maximized
    useEffect(() => {
        if (layoutState !== LayoutState.Maximize) return;

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            const container = widgetContainerRef.current;

            // If the element is no longer in the document, it means it was removed 
            // during the click event (e.g., closing a menu). We should ignore these 
            // to prevent the "click outside" logic from firing incorrectly.
            if (!document.documentElement.contains(target)) return;

            // Check if click is on a popup overlay or inside one (popups use position: fixed)
            const isPopupClick = target.closest('[class*="popupOverlay"]') ||
                target.closest('[class*="popup"]') ||
                target.closest('[role="dialog"]');
            if (isPopupClick) return;

            // If click is outside the widget container, close the chat
            if (container && !container.contains(target)) {
                setLayoutState(LayoutState.Close);
                onClose?.();
            }
        };

        // Add listener with a small delay to prevent immediate closing
        const timeoutId = setTimeout(() => {
            document.addEventListener('click', handleClickOutside);
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('click', handleClickOutside);
        };
    }, [layoutState, setLayoutState, onClose]);

    // Handle layout toggle for widget button
    const toggleWidget = () => {
        notificationService.unlockAudio();
        if (layoutState === LayoutState.Icon || layoutState === LayoutState.Close) {
            setLayoutState(LayoutState.Minimize);
        } else {
            setLayoutState(LayoutState.Icon);
        }
    };

    // Handle minimize
    const handleMinimize = () => {
        notificationService.unlockAudio();
        setLayoutState(LayoutState.Minibar);
    };

    // Handle maximize
    const handleMaximize = () => {
        notificationService.unlockAudio();
        if (layoutState === LayoutState.Maximize) {
            setLayoutState(LayoutState.Minimize);
        } else {
            setLayoutState(LayoutState.Maximize);
            setPositionState(PositionState.Widget);
        }
    };

    // Handle close
    const handleClose = () => {
        notificationService.unlockAudio();
        setLayoutState(LayoutState.Close);
        onClose?.();
    };

    // Fullscreen mode renders without widget chrome
    // Single conversation mode - hide left panel when only one room exists
    const isSingleConversationMode = rooms.length === 1;

    if (mode === 'fullscreen') {
        return (
            <div className={styles.fullscreenContainer}>
                {isInitializing ? (
                    <div className={styles.loadingContainer}>
                        <div className={styles.spinner}></div>
                        <p>{isBrowserOnline ? t.connectingToChat : t.checkInternetConnection}</p>
                    </div>
                ) : (
                    <>
                        <div className={styles.fullscreenLayout}>
                            {!isSingleConversationMode && <LeftPanel />}
                            <CenterPanel showSearchIcon={isSingleConversationMode} />
                            {selectedRoom && layoutState === LayoutState.Maximize && (
                                <RightPanel />
                            )}
                        </div>
                        {imageViewer && <ImageViewer />}
                        {videoViewer && <VideoViewer />}
                    </>
                )}
            </div>
        );
    }

    // Widget mode - show floating button when closed/icon
    // Hide chat icon if user is not an active member of the group
    if (layoutState === LayoutState.Close || layoutState === LayoutState.Icon) {
        if (!isActiveMember) {
            return null;
        }

        return (
            <>
                <button className={styles.floatingButton} onClick={toggleWidget}>
                    <MessageCircle size={24} />
                    {totalUnread > 0 && (
                        <span className={styles.iconBadge}>
                            {totalUnread > 99 ? '99+' : totalUnread}
                        </span>
                    )}
                </button>
                <MessageToast />
            </>
        );
    }

    // Widget mode - minibar (collapsed header only)
    if (layoutState === LayoutState.Minibar) {
        return (
            <>
            <MessageToast />
            <div className={`${styles.widgetContainer} ${styles.minibar}`}>
                <div className={styles.widgetHeader}>
                    <div className={styles.headerInfo}>
                        <span className={styles.headerTitle}>{t.chat}</span>
                        {shouldShowConnectionStatus && (
                            <span className={getConnectionStatusClassName()}>
                                {connectionStatusLabel}
                            </span>
                        )}
                    </div>
                    <div className={styles.headerActions}>
                        <button className={`${styles.headerBtn} ${styles.maximizeBtn}`} onClick={() => { notificationService.unlockAudio(); setLayoutState(LayoutState.Minimize); }} title={t.maximize}>
                            <Maximize2 size={16} />
                        </button>
                        <button className={styles.headerBtn} onClick={handleClose} title={t.close}>
                            <X size={16} />
                        </button>
                    </div>
                </div>
            </div>
            </>
        );
    }

    // Widget mode - minimize (windowed chat)
    const isMaximized = layoutState === LayoutState.Maximize;

    return (
        <div
            ref={widgetContainerRef}
            className={`${styles.widgetContainer} ${isMaximized ? styles.maximized : styles.minimized}`}
            onClick={() => notificationService.unlockAudio()}
        >
            {/* Header - only show when not maximized */}
            {!isMaximized && (
                <div className={styles.widgetHeader}>
                    <div className={styles.headerInfo}>
                        <span className={styles.headerTitle}>
                            {selectedRoom ? getRoomDisplayName(selectedRoom.groupName, selectedRoom.customerNo, 32) : t.chat}
                        </span>
                        {shouldShowConnectionStatus && (
                            <span className={getConnectionStatusClassName()}>
                                {connectionStatusLabel}
                            </span>
                        )}
                    </div>
                    <div className={styles.headerActions}>
                        <button className={styles.headerBtn} onClick={() => centerPanelRef.current?.toggleSearch()} title={t.searchMessages}>
                            <Search size={16} />
                        </button>
                        <button className={styles.headerBtn} onClick={() => centerPanelRef.current?.showMembers()} title={t.viewMembers}>
                            <Users size={16} />
                        </button>
                        <span className={styles.headerDivider} />
                        <button className={`${styles.headerBtn} ${styles.minimizeBtn}`} onClick={handleMinimize} title={t.minimize}>
                            <Minus size={16} />
                        </button>
                        <button className={`${styles.headerBtn} ${styles.maximizeBtn}`} onClick={handleMaximize} title={t.maximize}>
                            <Maximize2 size={16} />
                        </button>
                        <button className={styles.headerBtn} onClick={handleClose} title={t.close}>
                            <X size={16} />
                        </button>
                    </div>
                </div>
            )}

            {/* Content */}
            {isInitializing ? (
                <div className={styles.loadingContainer}>
                    <div className={styles.spinner}></div>
                    <p>{isBrowserOnline ? t.connecting : t.checkInternetConnection}</p>
                </div>
            ) : (
                isMaximized ? (
                    <div className={styles.desktopShell}>
                        <button
                            className={styles.maximizedCloseBtn}
                            onClick={handleClose}
                            title={t.close}
                            aria-label={t.close}
                        >
                            <X size={20} />
                        </button>
                        <div className={styles.desktopBody}>
                            {!isSingleConversationMode && <LeftPanel />}
                            <CenterPanel key="maximized" showSearchIcon={isSingleConversationMode} />
                            {selectedRoom && <RightPanel />}
                        </div>
                    </div>
                ) : (
                    <div className={styles.widgetContent}>
                        <CenterPanel ref={centerPanelRef} key="minimized" showSearchIcon={isSingleConversationMode} hideHeader />
                    </div>
                )
            )}

            {/* Viewers */}
            {imageViewer && <ImageViewer />}
            {videoViewer && <VideoViewer />}
            <MessageToast />
        </div>
    );
}
